import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LangProvider } from './contexts/LangContext';

// Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CompanyDashboard = lazy(() => import('./pages/CompanyDashboard'));
const TrainerPortal = lazy(() => import('./pages/TrainerPortal'));
const TrainerEarnings = lazy(() => import('./pages/TrainerEarnings'));
const SessionPage = lazy(() => import('./pages/SessionPage'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const MemberPortal = lazy(() => import('./pages/MemberPortal'));
const TrainerOnboarding = lazy(() => import('./pages/TrainerOnboarding'));
const TopicSelection = lazy(() => import('./pages/TopicSelection'));
const TrainerApply = lazy(() => import('./pages/TrainerApply'));
const TrainerApplications = lazy(() => import('./pages/TrainerApplications'));
const TrainerSchedule = lazy(() => import('./pages/TrainerSchedule'));
const SessionBooking = lazy(() => import('./pages/SessionBooking'));
const CommunicationHub = lazy(() => import('./pages/CommunicationHub'));
const PolicyManagement = lazy(() => import('./pages/PolicyManagement'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const PolicyViewPage = lazy(() => import('./pages/PoliciesPage').then((module) => ({ default: module.PolicyViewPage })));
const ResourceLibrary = lazy(() => import('./pages/ResourceLibrary'));
const OrgProfilePage = lazy(() => import('./pages/OrgProfilePage'));
const TrainerProfilePage = lazy(() => import('./pages/TrainerProfilePage'));
const LearnerProfilePage = lazy(() => import('./pages/LearnerProfilePage'));
const ProfileSettingsPage = lazy(() => import('./pages/ProfileSettingsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AdminSupportDashboard = lazy(() => import('./pages/AdminSupportDashboard'));
const ContentManagerPage = lazy(() => import('./pages/ContentManagerPage'));
const PaymentReturnPage = lazy(() => import('./pages/PaymentReturnPage'));
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage'));
const DemoRequirementPage = lazy(() => import('./pages/DemoRequirementPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'super_admin') return '/admin';
    if (user.role === 'company_admin') return '/dashboard';
    if (user.role === 'trainer') return '/trainer';
    if (user.role === 'member') return '/member';
    return '/login';
  };
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/demo/requirement" element={<DemoRequirementPage />} />
      <Route path="/requirement/:slug" element={
        <ProtectedRoute roles={['trainer', 'super_admin']}><DiscoveryPage /></ProtectedRoute>
      } />
      <Route path="/become-trainer" element={<TrainerApply />} />
      <Route path="/payment/return" element={<PaymentReturnPage />} />
      <Route path="/trainer-onboarding" element={
        <ProtectedRoute roles={['super_admin', 'company_admin']}><TrainerOnboarding /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute roles={['company_admin']}><CompanyDashboard /></ProtectedRoute>
      } />
      <Route path="/dashboard/topics" element={
        <ProtectedRoute roles={['company_admin']}><TopicSelection /></ProtectedRoute>
      } />
      <Route path="/dashboard/book" element={
        <ProtectedRoute roles={['company_admin']}><SessionBooking /></ProtectedRoute>
      } />
      <Route path="/trainer" element={
        <ProtectedRoute roles={['trainer']}><TrainerPortal /></ProtectedRoute>
      } />
      <Route path="/trainer/schedule" element={
        <ProtectedRoute roles={['trainer']}><TrainerSchedule /></ProtectedRoute>
      } />
      <Route path="/trainer/earnings" element={
        <ProtectedRoute roles={['trainer']}><TrainerEarnings /></ProtectedRoute>
      } />
      <Route path="/session/:sessionId" element={
        <ProtectedRoute roles={['trainer', 'company_admin', 'super_admin']}><SessionPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute roles={['super_admin']}><SuperAdminPanel /></ProtectedRoute>
      } />
      <Route path="/admin/trainer-applications" element={
        <ProtectedRoute roles={['super_admin']}><TrainerApplications /></ProtectedRoute>
      } />
      <Route path="/admin/communications" element={
        <ProtectedRoute roles={['super_admin']}><CommunicationHub /></ProtectedRoute>
      } />
      <Route path="/admin/policies" element={
        <ProtectedRoute roles={['super_admin']}><PolicyManagement /></ProtectedRoute>
      } />
      <Route path="/profile/settings" element={
        <ProtectedRoute roles={['trainer', 'company_admin', 'member']}><ProfileSettingsPage /></ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute roles={['trainer', 'company_admin', 'member']}><SupportPage /></ProtectedRoute>
      } />
      <Route path="/admin/support" element={
        <ProtectedRoute roles={['super_admin']}><AdminSupportDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute roles={['super_admin']}><ContentManagerPage /></ProtectedRoute>
      } />
      {/* Public profile pages */}
      <Route path="/org/:orgId" element={<OrgProfilePage />} />
      <Route path="/trainer/profile/:trainerId" element={<TrainerProfilePage />} />
      <Route path="/learner/:learnerId" element={<LearnerProfilePage />} />
      {/* Public policy pages */}
      <Route path="/policies" element={<PoliciesPage />} />
      <Route path="/policies/:slug" element={<PolicyViewPage />} />
      <Route path="/trainer/resources" element={
        <ProtectedRoute roles={['trainer']}><ResourceLibrary /></ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute roles={['member', 'company_admin', 'super_admin']}><ResourceLibrary /></ProtectedRoute>
      } />
      <Route path="/member" element={
        <ProtectedRoute roles={['member']}><MemberPortal /></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to={getDashboardPath()} replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
